// frontend/shared-ui/src/components/Lightbox.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

export default function Lightbox({
  images = [], // [{ url, thumbnailUrl, originalName }]
  index = 0,
  open = false,
  onClose,
  onIndexChange,
  onDelete, // optional: (image, idx) => void
}) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const containerRef = useRef(null);

  const current = images[index];

  // Reset zoom/pan when the active image changes
  useEffect(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [index, open]);

  const go = useCallback(
    (dir) => {
      if (!images.length) return;
      const next = (index + dir + images.length) % images.length;
      onIndexChange?.(next);
    },
    [index, images.length, onIndexChange],
  );

  // Keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") zoomBy(0.25);
      else if (e.key === "-") zoomBy(-0.25);
      else if (e.key === "0") resetZoom();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, go]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const zoomBy = (delta) => {
    setScale((s) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number((s + delta).toFixed(2)))),
    );
  };
  const resetZoom = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };

  // Wheel to zoom
  const onWheel = (e) => {
    if (!open) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setScale((s) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number((s + delta).toFixed(2)))),
    );
  };

  // Pan
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragging || !dragStart.current) return;
    setPos({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };
  const onMouseUp = () => {
    setDragging(false);
    dragStart.current = null;
  };

  // Touch (mobile swipe)
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.time;
    touchStart.current = null;
    if (dt > 500) return;
    if (Math.abs(dx) < 50 && Math.abs(dy) < 50) return;
    if (Math.abs(dx) > Math.abs(dy)) go(dx > 0 ? -1 : 1);
  };

  if (!open || !current) return null;

  return (
    <div
      className="fh-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === containerRef.current) onClose?.();
      }}
      ref={containerRef}
    >
      {/* Top bar */}
      <div className="fh-lightbox__topbar" onClick={(e) => e.stopPropagation()}>
        <div className="fh-lightbox__counter">
          {index + 1} / {images.length}
        </div>

        <div className="fh-lightbox__controls">
          <button
            type="button"
            className="fh-lightbox__btn"
            onClick={() => zoomBy(-0.25)}
            disabled={scale <= MIN_SCALE}
            title="Zoom out (-)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </button>

          <button
            type="button"
            className="fh-lightbox__btn fh-lightbox__btn--scale"
            onClick={resetZoom}
            title="Reset (0)"
          >
            {Math.round(scale * 100)}%
          </button>

          <button
            type="button"
            className="fh-lightbox__btn"
            onClick={() => zoomBy(0.25)}
            disabled={scale >= MAX_SCALE}
            title="Zoom in (+)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </button>

          {onDelete && (
            <button
              type="button"
              className="fh-lightbox__btn fh-lightbox__btn--danger"
              onClick={() => onDelete(current, index)}
              title="Delete image"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          )}

          <button
            type="button"
            className="fh-lightbox__btn"
            onClick={onClose}
            title="Close (Esc)"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          className="fh-lightbox__nav fh-lightbox__nav--prev"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {/* Image */}
      <div
        className={`fh-lightbox__stage ${dragging ? "is-dragging" : ""} ${scale > 1 ? "is-zoomable" : ""}`}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url || current.thumbnailUrl}
          alt={current.originalName || "image"}
          draggable={false}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: dragging ? "none" : "transform 150ms ease",
          }}
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          className="fh-lightbox__nav fh-lightbox__nav--next"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Thumbnails strip */}
      {images.length > 1 && (
        <div
          className="fh-lightbox__thumbs"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={img.id || img.uuid || i}
              type="button"
              className={`fh-lightbox__thumb ${i === index ? "is-active" : ""}`}
              onClick={() => onIndexChange?.(i)}
              aria-label={`Image ${i + 1}`}
            >
              <img src={img.thumbnailUrl || img.url} alt="" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
